// Derived 2026 NFL schedule indices: BYE_WEEKS_2026, SCHEDULE_BY_TEAM, GAMES_BY_WEEK.

import schedule from "./nfl-2026-schedule/nfl_2026_schedule.json";

const SEASON_WEEKS = 18;

// The schedule file and src/data/players.js disagree on two abbreviations: the
// schedule says JAX and LA, every player record says JAC and LAR. Left alone,
// that mismatch is silently wrong in two directions:
//   - BYE_WEEKS_2026[player.team] misses for every Jaguars and Rams player, so
//     the app showed no bye week at all for those two teams (the lookup is bare
//     at all five UI call sites — AuctionDraftBuilder, PlayerCard, PlayerList,
//     TeamGrid, ViewAllRostersModal).
//   - an `opp` read straight out of these indices prints the other spelling, so
//     the same franchise could appear as both JAC and JAX in one view.
// So the indices are normalized to the player-data spelling on the way in —
// including the opp/home/away fields, not just the keys — and that is the only
// form these exports ever emit. Fixing it here fixes every consumer at once.
const TEAM_CANON = { JAX: "JAC", LA: "LAR" };
const canon = (team) => TEAM_CANON[team] || team;

function buildIndices() {
  const byTeam = {};
  const byWeek = {};

  for (const g of schedule) {
    const home = canon(g.home_team);
    const away = canon(g.away_team);
    const common = {
      indoor: g.indoor === 1,
      neutral: g.neutral_site === 1,
      primetime: g.primetime ?? null,
      network: g.network,
      gameday: g.gameday_et,
      gametime: g.gametime_et,
    };

    if (!byTeam[home]) byTeam[home] = [];
    if (!byTeam[away]) byTeam[away] = [];
    byTeam[home].push({ week: g.week, opp: away, home: true,  ...common });
    byTeam[away].push({ week: g.week, opp: home, home: false, ...common });

    if (!byWeek[g.week]) byWeek[g.week] = [];
    byWeek[g.week].push({ home, away, ...common });
  }

  for (const team of Object.keys(byTeam)) {
    byTeam[team].sort((a, b) => a.week - b.week);
  }

  const byes = {};
  for (const team of Object.keys(byTeam)) {
    const played = new Set(byTeam[team].map(g => g.week));
    for (let w = 1; w <= SEASON_WEEKS; w++) {
      if (!played.has(w)) { byes[team] = w; break; }
    }
  }

  // Accept either spelling on lookup, so a caller holding a JAX/LA abbreviation
  // from some other source (src/data/adpData.js uses JAX) still resolves. These
  // are aliases of the canonical entries, not extra teams: iterate a schedule
  // index and you will see 34 keys for 32 franchises. Added after the bye pass
  // so the loop above counts each team once.
  for (const [alias, canonical] of Object.entries(TEAM_CANON)) {
    if (byTeam[canonical]) byTeam[alias] = byTeam[canonical];
    if (byes[canonical] !== undefined) byes[alias] = byes[canonical];
  }

  return { byTeam, byWeek, byes };
}

const { byTeam, byWeek, byes } = buildIndices();

export const SCHEDULE_BY_TEAM = byTeam;
export const GAMES_BY_WEEK    = byWeek;
export const BYE_WEEKS_2026   = byes;
